import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FileInputComponent} from 'ngx-material-file-input';
import {ReplaySubject} from 'rxjs';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FormHelperService} from '../../../../shared/services/form-helper.service';
import {DOCUMENT} from '@angular/common';
import {Router} from '@angular/router';
import {delay, first, map, takeUntil} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {FileBase64} from '../../../../shared/components/file-drag-and-drop/file-drag-and-drop.component';
import {CommissionImportService} from '../../../../shared/services/commission-import.service';
import {InsuranceCompany} from '../../../../shared/interface/insurance-company';
import {Dictionary} from '../../../../shared/interface/dictionary';
import moment from 'moment';
import {COMPANY_ASF_PREMIUM, DICT_TYPE_COMPANY, DictService} from "../../../../shared/services/dict.service";

@Component({
  selector: 'app-commission-import',
  templateUrl: './commission-import.component.html',
  styleUrls: ['./commission-import.component.scss']
})
export class CommissionImportComponent implements OnInit, OnDestroy {

  @ViewChild('fileInput') fileInputComponent: FileInputComponent;

  importCompanyId: ReplaySubject<number> = new ReplaySubject<number>(1);
  commissionImportFormValid = false;
  commissionImportForm: FormGroup;
  companyControl: FormControl;
  insuranceCompanyControl: FormControl;
  importerControl: FormControl;
  overCommissionControl: FormControl;
  profitControl: FormControl;
  cancellationControl: FormControl;
  additionalSettlementControl: FormControl;
  templateOverCommission = false;
  templateCancellation = false;
  templateAdditionalSettlement = false;
  fileControl: FormControl;
  billingMonthControl: FormControl;
  fileMaxSize = 5242880 * 20;
  processingRequest = false;
  formErrors = {};
  selectedInsuranceCompany: InsuranceCompany;
  typeRadioValue = 1;
  type1 = false;
  type2 = false;
  insuranceRequired = true;
  profitChecked = false;
  additionalSettlement = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);


  constructor(
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
    private commissionImportService: CommissionImportService,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private dictService: DictService,

  ) {
    const date = moment();
    date.startOf('month');
    date.subtract(1, 'month');

    this.commissionImportForm = fb.group({
      name: fb.control('', [Validators.required]),
      company: this.companyControl = fb.control('', [Validators.required]),
      overCommission: this.overCommissionControl = fb.control(''),
      profitImport: this.profitControl = fb.control(''),
      additionalSettlement: this.additionalSettlementControl = fb.control(''),
      cancellation: this.cancellationControl = fb.control('' ),
      insuranceCompany: this.insuranceCompanyControl = fb.control('', [Validators.required]),
      fileContent: fb.control('', [Validators.required]),
      importer: this.importerControl = fb.control('', [Validators.required]),
      billingMonth: this.billingMonthControl = fb.control(date, [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.commissionImportForm.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        delay(0)
      )
      .subscribe(() => {
        this.commissionImportFormValid = this.commissionImportForm.valid;
      });

    this.companyControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((companyId: number) => this.importCompanyId.next(companyId));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  import(): void {
    if (!this.commissionImportForm.valid) {
      this.commissionImportForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }

    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.commissionImportForm, this.formErrors);
    this.formErrors = {};
    const formData = {...this.commissionImportForm.value};
    formData.billingMonth = this.billingMonthControl.value.format('YYYY-MM');
    formData.company = this.companyControl.value;

    this.commissionImportService.import(formData)
      .pipe(
        first()
      )
      .subscribe(() => {
        this.snack.open('Rozpoczynanie importowania danych', '', {
          duration: 2000,
        });
        this.processingRequest = false;
        this.router.navigate(['/raporty-tu']);
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;

        this.snack.open('Podczas tworzenia importu wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.commissionImportForm, this.formErrors);
        }
      })
    ;
  }

  resetFileValue(): void {
    this.fileControl.setValue('');
    this.fileInputComponent.clear();
  }

  fileSelected(file: FileBase64): void {
    this.commissionImportForm.get('fileContent').setValue(file.base64);
    this.commissionImportForm.get('name').setValue(file.filename);
  }

  onAvailableImportersChange(items: Dictionary[]): void {
    if (items.length > 0) {
      this.importerControl.enable();
    } else {
      this.importerControl.disable();
    }
  }

  slideChange($event, type): void{
    this.companyControl.enable();
    this.typeRadioValue = type;

    if (type === 1) {
      this.insuranceRequired = true;
      if ($event.checked) {
        this.type1 = true; // overCommissionControl
        this.type2 = false; // cancellationControl
        this.profitChecked = false; // profitControl

        this.cancellationControl.setValue(false);
        this.profitControl.setValue(false);
        this.additionalSettlementControl.setValue(false);

        // template
        this.templateOverCommission = true;
        this.templateCancellation = false;
        this.templateAdditionalSettlement = false;

        this.insuranceRequired = false;
        this.insuranceCompanyControl.setValidators([]);
        this.importerControl.setValidators([]);

      } else {
        this.type1 = false; // overCommissionControl
        this.type2 = false; // cancellationControl
        this.profitChecked = false; // profitControl

        this.cancellationControl.setValue(false);
        this.profitControl.setValue(false);
        this.additionalSettlementControl.setValue(false);

        // template
        this.templateCancellation = false;
        this.templateOverCommission = false;
        this.templateAdditionalSettlement = false;

        this.insuranceRequired = true;
        this.insuranceCompanyControl.setValidators([Validators.required]);
        this.importerControl.setValidators([Validators.required]);

      }
    } else if (type === 2 ) {
      if ($event.checked) {
        this.type1 = false; // overCommissionControl
        this.type2 = true; // cancellationControl
        this.profitChecked = false; // profitControl

        this.overCommissionControl.setValue(false);
        this.profitControl.setValue(false);
        this.additionalSettlementControl.setValue(false);

        // template
        this.templateCancellation = true;
        this.templateOverCommission = false;
        this.templateAdditionalSettlement = false;

      } else {
        this.type1 = false; // overCommissionControl
        this.type2 = false; // cancellationControl
        this.profitChecked = false; // profitControl
        this.overCommissionControl.setValue(false);
        this.profitControl.setValue(false);
        this.additionalSettlementControl.setValue(false);

         // template
        this.templateCancellation = false;
        this.templateOverCommission = false;
        this.templateAdditionalSettlement = false;

      }
    } else if (type === 3 ) {
      if ($event.checked) {
        this.type1 = false; // overCommissionControl
        this.type2 = false; // cancellationControl
        this.profitChecked = true; // profitControl

        this.overCommissionControl.setValue(false);
        this.cancellationControl.setValue(false);
        this.additionalSettlementControl.setValue(false);

        // template
        this.templateCancellation = false;
        this.templateOverCommission = false;
        this.templateAdditionalSettlement = false;

        this.companyControl.disable();
        this.companyDict();

        this.insuranceRequired = true;
        this.insuranceCompanyControl.setValidators([Validators.required]);
        this.importerControl.setValidators([Validators.required]);

      } else {
        this.type1 = false; // overCommissionControl
        this.type2 = false; // cancellationControl
        this.profitChecked = false; // profitControl
        this.additionalSettlement = false; // Dodatkowe rozliczenie
        this.overCommissionControl.setValue(false);
        this.cancellationControl.setValue(false);
        this.additionalSettlementControl.setValue(false);

        // template
        this.templateCancellation = false;
        this.templateOverCommission = false;
        this.templateAdditionalSettlement = false;

        this.companyControl.enable();

        this.insuranceRequired = true;
        this.insuranceCompanyControl.setValidators([Validators.required]);
        this.importerControl.setValidators([Validators.required]);

      }
    } else {
      if ($event.checked) {
        this.type1 = false; // overCommissionControl
        this.type2 = false; // cancellationControl
        this.profitChecked = false; // profitControl
        this.additionalSettlement = true; // Dodatkowe rozliczenie
        this.overCommissionControl.setValue(false);
        this.cancellationControl.setValue(false);
        this.profitControl.setValue(false);

        // template
        this.templateCancellation = false;
        this.templateOverCommission = false;
        this.templateAdditionalSettlement = true;

        // this.companyDict();

        this.insuranceRequired = false;
        this.insuranceCompanyControl.setValidators([]);
        this.importerControl.setValidators([]);

      } else {
        this.type1 = false; // overCommissionControl
        this.type2 = false; // cancellationControl
        this.profitChecked = false; // profitControl
        this.additionalSettlement = true; // Dodatkowe rozliczenie

        this.cancellationControl.setValue(false);
        this.profitControl.setValue(false);
        this.additionalSettlementControl.setValue(false);

        // template
        this.templateOverCommission = false;
        this.templateCancellation = false;
        this.templateAdditionalSettlement = false;

        this.insuranceRequired = false;
        this.insuranceCompanyControl.setValidators([]);
        this.importerControl.setValidators([]);
      }
    }
  }
  companyDict(): void {
    this.dictService.getDictSubject()
      .pipe(
        first(),
        map((dicts: Dictionary[]) => {
          return dicts.find((dict: Dictionary) => dict.type === DICT_TYPE_COMPANY && dict.key === COMPANY_ASF_PREMIUM);
        })
      ).subscribe((dict: Dictionary) => {
      this.companyControl.setValue(dict.id);
    });
  }

}
